import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { MailCheck, Sparkles, Loader2 } from "lucide-react";

const CATEGORY_LABEL = {
  data_access: "Data access",
  data_correction: "Data correction",
  data_deletion: "Data deletion",
  consent_withdrawal: "Consent withdrawal",
  unauthorized_processing: "Unauthorised processing",
  other: "Other",
};

const STATUS_STYLES = {
  open: "bg-accent/15 text-accent",
  in_review: "bg-primary/10 text-primary",
  resolved: "bg-secondary/15 text-secondary",
  rejected: "bg-muted text-muted-foreground",
};

export default function AdminGrievances() {
  const [grievances, setGrievances] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    const { data } = await api.get("/admin/grievances", { params });
    setGrievances(data);
  };
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <AppShell
      pageTitle="DPDP Grievances"
      pageDescription="Grievance ledger with 30-day statutory SLA. Every action is logged."
      actions={
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="grievances-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_review">In review</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      {grievances.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <MailCheck size={28} className="mx-auto text-muted-foreground" />
          <div className="mt-3 font-serif text-xl text-primary">No grievances</div>
          <p className="text-sm text-muted-foreground mt-1">Nothing to review right now.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-hidden" data-testid="grievances-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Ticket</th>
                <th className="px-4 py-3">Filed by</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Filed</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grievances.map((g) => (
                <GrievanceRow key={g.id} g={g} onSaved={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}

function GrievanceRow({ g, onSaved }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(g.status);
  const [note, setNote] = useState(g.resolution_note || "");
  const [saving, setSaving] = useState(false);
  const [triage, setTriage] = useState(g.ai_triage || null);
  const [triaging, setTriaging] = useState(false);

  const runTriage = async () => {
    setTriaging(true);
    try {
      const { data } = await api.post(`/ai/grievance/${g.id}/triage`, {});
      setTriage(data.triage);
      toast.success("AI triage ready");
    } catch { toast.error("Triage failed — try again"); }
    finally { setTriaging(false); }
  };

  const useDraft = () => {
    if (triage?.draft_reply) {
      setNote(triage.draft_reply);
      toast.success("Draft inserted into resolution note");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/grievances/${g.id}`, { status, resolution_note: note });
      toast.success("Grievance updated");
      setOpen(false);
      onSaved();
    } catch { toast.error("Could not update"); }
    finally { setSaving(false); }
  };

  return (
    <tr className="border-t border-border" data-testid={`grievance-row-${g.id}`}>
      <td className="px-4 py-3 font-mono text-xs text-primary">{g.id}</td>
      <td className="px-4 py-3">
        <div className="font-medium text-primary">{g.name}</div>
        <div className="text-xs text-muted-foreground">{g.email}</div>
      </td>
      <td className="px-4 py-3">{CATEGORY_LABEL[g.category] || g.category}</td>
      <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{g.subject}</td>
      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(g.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</td>
      <td className="px-4 py-3">
        <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${STATUS_STYLES[g.status] || STATUS_STYLES.open}`}>
          {g.status.replace("_", " ")}
        </span>
      </td>
      <td className="px-4 py-3">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid={`grievance-open-${g.id}`}>Review</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Grievance {g.id}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="overline text-[10px]">Subject</div>
                <div className="text-sm font-medium text-primary">{g.subject}</div>
              </div>
              <div>
                <div className="overline text-[10px]">Description</div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{g.description}</p>
              </div>

              <div className="rounded-md border border-primary/20 bg-primary/5 p-3" data-testid={`grievance-triage-${g.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={13} className="text-primary" />
                    <span className="text-[11px] uppercase tracking-widest text-primary">AI triage</span>
                  </div>
                  {!triage ? (
                    <Button size="sm" variant="outline" onClick={runTriage} disabled={triaging} data-testid={`grievance-triage-run-${g.id}`}>
                      {triaging ? <><Loader2 size={12} className="mr-1 animate-spin" /> Analysing…</> : "Classify & draft"}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={useDraft} data-testid={`grievance-triage-use-${g.id}`}>
                      Use draft reply
                    </Button>
                  )}
                </div>
                {triage ? (
                  <div className="mt-3 space-y-2 text-xs">
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full border border-primary/30 bg-white px-2 py-0.5 text-primary">
                        {(CATEGORY_LABEL[triage.category] || triage.category).toString()}
                      </span>
                      <span className={`rounded-full border px-2 py-0.5 ${
                        triage.severity === "high" ? "bg-rose-50 border-rose-200 text-rose-800" :
                        triage.severity === "medium" ? "bg-amber-50 border-amber-200 text-amber-800" :
                        "bg-emerald-50 border-emerald-200 text-emerald-800"
                      }`}>
                        {triage.severity} severity
                      </span>
                    </div>
                    {triage.next_steps?.length ? (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Internal next steps</div>
                        <ul className="mt-1 space-y-0.5">
                          {triage.next_steps.map((n, i) => (
                            <li key={i} className="text-primary flex items-start gap-1.5"><span>•</span>{n}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {triage.draft_reply ? (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Draft reply (preview)</div>
                        <p className="mt-1 text-muted-foreground italic line-clamp-3">{triage.draft_reply}</p>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">Click "Classify & draft" to let the AI suggest a category, severity, and reply template.</p>
                )}
              </div>

              <div>
                <div className="overline text-[10px]">Status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid={`grievance-status-${g.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_review">In review</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="overline text-[10px]">Resolution note (visible to DPO only)</div>
                <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} data-testid={`grievance-note-${g.id}`} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid={`grievance-save-${g.id}`}>{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </td>
    </tr>
  );
}
